import { useState } from "react";
import { Layout } from "@jambushrushti/components/layout/Layout";
import { useArticlesFromDB } from "@jambushrushti/hooks/useContent";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Link } from "react-router-dom";
import {
  Search,
  FileText,
  User,
  ArrowRight,
  ExternalLink,
  ScrollText,
  Loader2,
} from "lucide-react";
import { format } from "date-fns";

const Articles = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const { data: articles = [], isLoading } = useArticlesFromDB();

  const filteredArticles = articles.filter(article => {
    const matchesSearch =
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (article.excerpt || "").toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-hero lotus-pattern py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="animate-fade-up mx-auto max-w-3xl text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-secondary/80 px-4 py-2 text-sm">
              <ScrollText className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">
                {articles.length} Articles
              </span>
            </div>
            <h1 className="font-heading mb-6 text-4xl font-bold text-foreground sm:text-5xl">
              Articles & Tributes
            </h1>
            <p className="font-body text-lg text-muted-foreground">
              Scholarly articles, heartfelt tributes, and important documents
              celebrating the legacy of Gurudev Muni Jambuvijayaji Maharaj
              Saheb.
            </p>
          </div>
        </div>
      </section>

      {/* Search */}
      <section className="border-b border-border bg-background py-8">
        <div className="container mx-auto px-4">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search articles..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="bg-background py-12 lg:py-16">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="mb-6">
                <p className="text-muted-foreground">
                  Showing {filteredArticles.length} articles
                </p>
              </div>

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filteredArticles.map((article, index) => (
                  <Link
                    key={article.id}
                    to={`/articles/${article.id}`}
                    className="group"
                  >
                    <Card
                      variant="interactive"
                      className="animate-fade-up h-full overflow-hidden"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className="aspect-[4/3] overflow-hidden">
                        <img
                          src={
                            article.cover_image ||
                            "https://siddhijambuparivar.com/wp-content/uploads/2022/11/96-min.jpg"
                          }
                          alt={article.title}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <CardContent className="p-5">
                        <div className="mb-3 flex items-center gap-2">
                          {article.category && (
                            <Badge variant="default">{article.category}</Badge>
                          )}
                        </div>
                        <h3 className="font-heading mb-1 line-clamp-2 text-lg font-semibold text-foreground transition-colors group-hover:text-primary">
                          {article.title}
                        </h3>
                        <p className="font-body mb-3 line-clamp-2 text-base text-muted-foreground">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="font-body flex items-center gap-2 text-base text-muted-foreground">
                            <User className="h-3 w-3" />
                            <span>{article.author || "Research Team"}</span>
                          </div>
                          <span className="font-body flex items-center gap-1 text-base text-primary">
                            Read More
                            <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>

              {filteredArticles.length === 0 && (
                <div className="py-16 text-center">
                  <FileText className="mx-auto mb-4 h-16 w-16 text-muted-foreground/30" />
                  <h3 className="font-heading mb-2 text-xl font-semibold text-foreground">
                    No articles found
                  </h3>
                  <p className="font-body mb-4 text-base text-muted-foreground">
                    Try adjusting your search criteria
                  </p>
                  <Button variant="outline" onClick={() => setSearchQuery("")}>
                    Clear Search
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* External Link */}
      <section className="bg-secondary/30 py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-heading mb-4 text-xl font-bold text-foreground">
            More Content Available
          </h2>
          <p className="font-body mx-auto mb-6 max-w-lg text-base text-muted-foreground">
            Visit the official Siddhi Jambu Parivar website for additional
            articles and documents.
          </p>
          <Button variant="outline" asChild>
            <a
              href="https://siddhijambuparivar.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Visit Official Website
              <ExternalLink className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-spiritual py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-heading mb-4 text-2xl font-bold text-foreground">
            Explore More
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="outline" asChild>
              <Link to="/books">Browse Books</Link>
            </Button>
            <Button variant="hero" asChild>
              <Link to="/about/gurudev">
                About Gurudev
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/community/blog">Read Blog</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Articles;
